import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import RecipientPicker, { CUSTOM_TIER } from './RecipientPicker';
import SmsCompose from './SmsCompose';
import SmsSegmentCounter from './SmsSegmentCounter';
import {
  computeBuckets, eligibleFor, excludedFor, splitByChannel, TEXT_REASON_LABELS,
  SEASON_FILTERS, filterBySeason,
} from '../utils/recipientBuckets';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// The merge fields the server fills in per recipient (server/services/emailMerge.js).
const MERGE_FIELDS = [
  'customer_name', 'property_address', 'company_name', 'amount_due',
  'driveway_size', 'snow_price', 'salt_price', 'service_preference',
  'rates_link', 'portal_link',
  // The season Yes/No links: a token is made for each recipient when sent.
  'season_yes_link', 'season_no_link',
];

const BATCH_POLL_MS = 2000;

const dollars = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;
const reasonList = (counts) => Object.entries(counts || {})
  .map(([reason, n]) => `${n} ${(TEXT_REASON_LABELS[reason] || reason).toLowerCase()}`)
  .join(', ');

// Bulk email, and with SMS_ENABLED on, bulk texts. The channel picker (Email,
// Text, Both) is shown only when smsEnabled is passed. Email goes through
// POST /api/email/send exactly as before; texts are queued by POST
// /api/sms/send (202 and a batch) and sent in the background by the server's
// worker, so the sending step polls GET /api/sms/batches/:id. "Both" sends
// each customer what they can receive: the email part to those who can be
// emailed, the text part to those who can be texted.
//
// canSubscribe: owner and admin, the roles PUT /api/customers/:id accepts.
// onCustomerSubscribed(id) lets the page update its own copy of the row.
// timezone dates the "unsubscribed themselves on" confirm.
//
// Every email carries an unsubscribe link and the business mailing address
// (server/services/customerUnsubscribe.js). The preview shows that footer, and
// a send the server would refuse (no business address) shows the same warning
// in the preview step.
function EmailCustomersModal({
  show, onClose, customers = [], routes = [], pricingTiers = [],
  canSubscribe = false, onCustomerSubscribed, smsEnabled = false, timezone,
}) {
  const [step, setStep] = useState(1); // 1=recipients, 2=compose, 3=preview, 4=sending
  const [channel, setChannel] = useState('email');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [smsBody, setSmsBody] = useState('');
  const [filterMode, setFilterMode] = useState('all_opted_in');
  const [filterRoute, setFilterRoute] = useState('');
  const [filterTier, setFilterTier] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  // Customers subscribed from this screen since it opened. The customers prop
  // is the page's copy and may not have caught up yet, so this set is what
  // moves the row into the selectable list immediately.
  const [subscribedIds, setSubscribedIds] = useState(new Set());
  const [subscribingId, setSubscribingId] = useState(null);
  // The previews are rendered by the server for one real recipient, with the
  // same code the send uses, so what is shown is what that customer receives.
  const [previewId, setPreviewId] = useState('');
  const [preview, setPreview] = useState({ loading: false, subject: '', body: '', footer: '', warning: null, error: '' });
  const [smsPreviewId, setSmsPreviewId] = useState('');
  const [smsPreview, setSmsPreview] = useState({ loading: false, data: null, error: '' });
  // Where customers' replies go (the send's Reply-To). undefined while loading
  // or after a failed load, which shows nothing; null means no address is set.
  const [replyTo, setReplyTo] = useState(undefined);
  // The send's refusal before it is tried (no business mailing address), from
  // the same lookup; null when email can go.
  const [sendWarning, setSendWarning] = useState(null);
  // The text part's outcome: the 202 body, then the polled batch.
  const [smsQueued, setSmsQueued] = useState(null);
  const [batch, setBatch] = useState(null);
  const [emailError, setEmailError] = useState('');

  useEffect(() => {
    if (show) {
      loadTemplates();
      loadReplyTo();
      // Reset state when modal opens
      setStep(1);
      setChannel('email');
      setSelectedTemplate(null);
      setSubject('');
      setBody('');
      setSmsBody('');
      setFilterMode('all_opted_in');
      setFilterRoute('');
      setFilterTier('');
      setSelectedIds(new Set());
      setResult(null);
      setError('');
      setSubscribedIds(new Set());
      setSubscribingId(null);
      setSmsQueued(null);
      setBatch(null);
      setEmailError('');
    }
  }, [show]);

  const loadTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/email/templates`);
      setTemplates(response.data);
    } catch (err) {
      console.error('Failed to load templates:', err.message);
    }
  };

  const loadReplyTo = async () => {
    setReplyTo(undefined);
    try {
      const response = await axios.get(`${API_URL}/api/email/reply-to`);
      setReplyTo(response.data.replyTo || null);
      setSendWarning(response.data.warning || null);
    } catch (err) {
      console.error('Failed to load reply address:', err.message);
    }
  };

  // Disjoint buckets per channel, and who is excluded and why
  // (utils/recipientBuckets.js holds the rules and the reasoning).
  const buckets = computeBuckets(customers, subscribedIds);
  const eligibleCustomers = eligibleFor(channel, buckets);
  const excludedCustomers = excludedFor(channel, buckets);
  const wantsEmail = channel !== 'text';
  const wantsText = channel !== 'email';

  const getRecipients = useCallback(() => {
    let filtered = eligibleCustomers;

    switch (filterMode) {
      case 'all_opted_in':
        break;
      case 'active_only':
        filtered = filtered.filter(c => c.is_active !== false);
        break;
      case 'by_route':
        if (filterRoute) {
          const route = routes.find(r => r.id === parseInt(filterRoute));
          if (route && route.stops) {
            const routeCustomerIds = new Set(route.stops.map(s => s.customer_id));
            filtered = filtered.filter(c => routeCustomerIds.has(c.id));
          }
        }
        break;
      // Filters on the snow_tier_id foreign key, never the legacy tier text.
      // CUSTOM_TIER is the literal string 'custom'; a pricing_tiers.id is
      // always a positive integer, so the sentinel can never collide with one.
      case 'by_tier':
        if (filterTier === CUSTOM_TIER) {
          filtered = filtered.filter(c => !c.snow_tier_id);
        } else if (filterTier) {
          filtered = filtered.filter(c => String(c.snow_tier_id) === filterTier);
        }
        break;
      case 'selected':
        filtered = filtered.filter(c => selectedIds.has(c.id));
        break;
      default:
        if (SEASON_FILTERS[filterMode]) filtered = filterBySeason(filtered, filterMode);
        break;
    }

    return filtered;
  }, [eligibleCustomers, filterMode, filterRoute, filterTier, selectedIds, routes]);

  const recipients = getRecipients();
  const split = splitByChannel(channel, recipients, buckets);

  const toggleCustomer = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Same column and same endpoint as the edit modal's "Subscribed to emails"
  // box: a one-key body, so PUT writes email_subscribed and nothing else. The
  // row becomes selectable but is not ticked; adding them to this send stays
  // the owner's choice.
  const subscribeCustomer = async (id) => {
    setSubscribingId(id);
    setError('');
    try {
      await axios.put(`${API_URL}/api/customers/${id}`, { emailSubscribed: true });
      setSubscribedIds(prev => new Set(prev).add(id));
      if (onCustomerSubscribed) onCustomerSubscribed(id);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to subscribe customer');
    } finally {
      setSubscribingId(null);
    }
  };

  const applyTemplate = (template) => {
    setSelectedTemplate(template);
    setSubject(template.subject);
    setBody(template.body);
  };

  const insertMergeField = (field) => {
    setBody(prev => prev + `{{${field}}}`);
  };

  const previewRecipientId = previewId && split.email.some(r => String(r.id) === previewId)
    ? previewId
    : (split.email[0] ? String(split.email[0].id) : '');
  const smsPreviewRecipientId = smsPreviewId && split.text.some(r => String(r.id) === smsPreviewId)
    ? smsPreviewId
    : (split.text[0] ? String(split.text[0].id) : '');
  const textIdsKey = split.text.map(r => r.id).join(',');

  useEffect(() => {
    if (step !== 3 || !wantsEmail || !previewRecipientId) return undefined;
    let cancelled = false;
    setPreview(p => ({ ...p, loading: true, error: '' }));
    axios.post(`${API_URL}/api/email/preview`, { subject, body, recipient_id: Number(previewRecipientId) })
      .then((res) => {
        if (!cancelled) {
          setPreview({
            loading: false, subject: res.data.subject, body: res.data.body,
            footer: res.data.footer || '', warning: res.data.warning || null, error: '',
          });
        }
      })
      .catch((err) => {
        if (!cancelled) setPreview({ loading: false, subject: '', body: '', footer: '', warning: null, error: err.response?.data?.error || 'Preview could not be loaded' });
      });
    return () => { cancelled = true; };
  }, [step, wantsEmail, previewRecipientId, subject, body]);

  // The text preview also carries the whole send's totals (segments, cost,
  // who the server would skip), the quiet hours state and the daily cap.
  useEffect(() => {
    if (step !== 3 || !wantsText || !smsPreviewRecipientId) return undefined;
    let cancelled = false;
    setSmsPreview(p => ({ ...p, loading: true, error: '' }));
    axios.post(`${API_URL}/api/sms/preview`, {
      body: smsBody,
      recipient_id: Number(smsPreviewRecipientId),
      recipient_ids: textIdsKey.split(',').map(Number),
    })
      .then((res) => { if (!cancelled) setSmsPreview({ loading: false, data: res.data, error: '' }); })
      .catch((err) => {
        if (!cancelled) setSmsPreview({ loading: false, data: null, error: err.response?.data?.error || 'Preview could not be loaded' });
      });
    return () => { cancelled = true; };
  }, [step, wantsText, smsPreviewRecipientId, smsBody, textIdsKey]);

  // The sending step polls the batch until the worker has settled every row.
  const batchFinished = Boolean(batch && batch.finished);
  useEffect(() => {
    if (step !== 4 || !smsQueued || batchFinished) return undefined;
    let cancelled = false;
    const poll = () => axios.get(`${API_URL}/api/sms/batches/${smsQueued.batchId}`)
      .then((res) => { if (!cancelled) setBatch(res.data); })
      .catch(() => {});
    poll();
    const timer = setInterval(poll, BATCH_POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [step, smsQueued, batchFinished]);

  const smsData = smsPreview.data;
  const smsBlocked = wantsText && split.text.length > 0 && (
    !smsData || smsPreview.loading || !smsData.body || !smsData.totals
    || smsData.quietHours?.quiet || (smsData.sender && !smsData.sender.ok)
    || (smsData.dailyCap && !smsData.dailyCap.ok) || smsData.totals.recipients === 0
  );

  // WHY AN EXCLUDED CUSTOMER CANNOT REACH THESE PAYLOADS
  //
  // Both send lists come from `recipients`, which is getRecipients(). That
  // opens with `let filtered = eligibleCustomers` and every branch either
  // leaves `filtered` alone or narrows it with .filter; nothing adds a row,
  // and excludedCustomers is never read. split.email and split.text then
  // narrow `recipients` again to the email-eligible and text-eligible. So a
  // poisoned selectedIds cannot add anyone: the 'selected' branch filters the
  // eligible BY selectedIds rather than mapping ids back to customers.
  //
  // Asserted by client/src/components/EmailCustomersModal.test.js, which
  // drives the real component and inspects the axios.post bodies. The server
  // re-checks everything on send in any case.
  const handleSend = async () => {
    if (wantsEmail && split.email.length > 0 && (!subject.trim() || !body.trim())) {
      setError('Subject and body are required');
      return;
    }
    if (recipients.length === 0) {
      setError('No recipients selected');
      return;
    }

    setSending(true);
    setError('');
    setEmailError('');
    setStep(4);

    // Texts first: they are the part that can be refused (quiet hours, the
    // daily cap), and a refusal must stop the whole send before any email
    // goes out.
    if (wantsText && split.text.length > 0) {
      try {
        const response = await axios.post(`${API_URL}/api/sms/send`, {
          recipient_ids: split.text.map(r => r.id),
          body: smsBody,
          channel: channel === 'both' ? 'both' : 'sms',
        });
        setSmsQueued(response.data);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to send texts');
        setStep(3);
        setSending(false);
        return;
      }
    }

    if (wantsEmail && split.email.length > 0) {
      try {
        const response = await axios.post(`${API_URL}/api/email/send`, {
          recipient_ids: split.email.map(r => r.id),
          subject,
          body,
          template_name: selectedTemplate?.name || null,
        });
        setResult(response.data);
      } catch (err) {
        const message = err.response?.data?.error || 'Failed to send emails';
        if (channel === 'email') {
          setError(message);
          setStep(3);
        } else {
          setEmailError(message);
        }
      }
    }
    setSending(false);
  };

  if (!show) return null;

  const composeReady = (!wantsEmail || split.email.length === 0 || (subject.trim() && body.trim()))
    && (!wantsText || split.text.length === 0 || smsBody.trim());

  const sendLabel = channel === 'text'
    ? `Send ${split.text.length} Text${split.text.length === 1 ? '' : 's'}`
    : `Send to ${recipients.length} Customers`;

  // "Sent: 12 emails, 15 texts", naming only the channels that went.
  const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
  const sentParts = [
    ...(result ? [plural(result.sent ?? 0, 'email')] : []),
    ...(smsQueued ? [plural(smsQueued.queued ?? 0, 'text')] : []),
  ];
  const sentHeading = sentParts.length ? `Sent: ${sentParts.join(', ')}` : 'Sent!';

  const batchCount = (...statuses) => statuses.reduce((n, s) => n + ((batch && batch.counts[s]) || 0), 0);

  return (
    <div className="modal-overlay" onClick={onClose}>
      {/* `modal`, not `modal-content`: `.modal` is the app's modal surface
          (white background, radius, scroll containment). */}
      <div
        className="modal"
        style={{ maxWidth: '700px', maxHeight: '85vh', overflow: 'auto' }}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-header">
          <h2>
            {step === 1 && 'Select Recipients'}
            {step === 2 && (channel === 'email' ? 'Compose Email' : channel === 'text' ? 'Compose Text' : 'Compose Email and Text')}
            {step === 3 && 'Preview & Send'}
            {step === 4 && (sending ? 'Sending...' : sentHeading)}
          </h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        {error && <div className="alert alert-error" style={{ margin: '0 24px' }}>{error}</div>}

        <div style={{ padding: '16px 24px' }}>
          {/* Step indicator */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', justifyContent: 'center' }}>
            {[1, 2, 3].map(s => (
              <div key={s} style={{
                width: '32px', height: '32px', borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '14px', fontWeight: 600,
                background: step >= s ? 'var(--color-primary, #1e88e5)' : '#e0e0e0',
                color: step >= s ? '#fff' : 'var(--color-text-light)',
              }}>{s}</div>
            ))}
          </div>

          {step === 1 && (
            <RecipientPicker
              channel={channel}
              onChannelChange={(c) => { setChannel(c); setSelectedIds(new Set()); }}
              smsEnabled={smsEnabled}
              buckets={buckets}
              eligible={eligibleCustomers}
              excluded={excludedCustomers}
              recipients={recipients}
              split={split}
              filterMode={filterMode} setFilterMode={setFilterMode}
              filterRoute={filterRoute} setFilterRoute={setFilterRoute}
              filterTier={filterTier} setFilterTier={setFilterTier}
              routes={routes}
              pricingTiers={pricingTiers}
              selectedIds={selectedIds}
              onToggle={toggleCustomer}
              onSelectAll={() => setSelectedIds(new Set(eligibleCustomers.map(c => c.id)))}
              onSelectNone={() => setSelectedIds(new Set())}
              canSubscribe={canSubscribe}
              subscribingId={subscribingId}
              onSubscribe={subscribeCustomer}
              timezone={timezone}
            />
          )}

          {/* Step 2: Compose */}
          {step === 2 && (
            <div>
              {wantsEmail && sendWarning && (
                <div className="alert alert-error" role="alert" data-testid="email-send-warning">
                  {sendWarning.error}{' '}
                  {sendWarning.code === 'BUSINESS_ADDRESS_REQUIRED' && (
                    <Link to="/settings" onClick={onClose}>Open Settings</Link>
                  )}
                </div>
              )}

              {wantsEmail && replyTo !== undefined && (
                <div style={{ fontSize: '13px', color: 'var(--color-text-light)', marginBottom: '12px' }}>
                  {replyTo ? (
                    <>Replies go to: <strong>{replyTo}</strong></>
                  ) : (
                    <>
                      Replies go to: no address set.{' '}
                      <Link to="/settings" onClick={onClose}>Add a business email in Settings</Link>
                    </>
                  )}
                </div>
              )}

              {wantsEmail && (
                <>
                  {channel === 'both' && <h3 style={{ fontSize: '1rem', margin: '0 0 8px' }}>Email ({split.email.length})</h3>}
                  {templates.length > 0 && (
                    <div className="form-group">
                      <label className="form-label">Use Template</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {templates.map(t => (
                          <button
                            key={t.id}
                            className={`btn btn-sm ${selectedTemplate?.id === t.id ? 'btn-primary' : 'btn-outline'}`}
                            onClick={() => applyTemplate(t)}
                          >
                            {t.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="form-group">
                    <label className="form-label">Subject</label>
                    <input
                      type="text"
                      className="form-input"
                      value={subject}
                      onChange={e => setSubject(e.target.value)}
                      placeholder="Email subject line..."
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Body</label>
                    <textarea
                      className="form-textarea"
                      value={body}
                      onChange={e => setBody(e.target.value)}
                      rows={10}
                      placeholder="Write your email here..."
                      style={{ fontFamily: 'inherit', lineHeight: '1.6' }}
                    />
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--color-text-light)', lineHeight: '28px' }}>Insert:</span>
                    {MERGE_FIELDS.map(field => (
                      <button
                        key={field}
                        className="btn btn-sm btn-outline"
                        style={{ fontSize: '11px', padding: '2px 8px' }}
                        onClick={() => insertMergeField(field)}
                      >
                        {`{{${field}}}`}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {wantsText && (
                <div style={{ marginTop: wantsEmail ? '24px' : 0 }}>
                  {channel === 'both' && <h3 style={{ fontSize: '1rem', margin: '0 0 8px' }}>Text ({split.text.length})</h3>}
                  <SmsCompose body={smsBody} onChange={setSmsBody} mergeFields={MERGE_FIELDS} />
                </div>
              )}
            </div>
          )}

          {/* Step 3: Preview */}
          {step === 3 && (
            <div>
              {wantsEmail && split.email.length > 0 && (
                <>
                  {channel === 'both' && <h3 style={{ fontSize: '1rem', margin: '0 0 8px' }}>Email</h3>}
                  <div className="form-group">
                    <label className="form-label" htmlFor="emailPreviewAs">Preview as</label>
                    <select
                      id="emailPreviewAs"
                      className="form-select"
                      value={previewRecipientId}
                      onChange={e => setPreviewId(e.target.value)}
                    >
                      {split.email.map(r => (
                        <option key={r.id} value={String(r.id)}>
                          {`${r.first_name || ''} ${r.last_name || ''}`.trim() || r.email}
                        </option>
                      ))}
                    </select>
                    <div style={{ fontSize: '12px', color: 'var(--color-text-light)', marginTop: '4px' }}>
                      Every field is filled in for each customer separately, as shown here.
                    </div>
                  </div>
                  <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '20px', marginBottom: '16px' }}>
                    <div style={{ borderBottom: '1px solid #e0e0e0', paddingBottom: '12px', marginBottom: '12px' }}>
                      <div style={{ fontSize: '12px', color: 'var(--color-text-light)' }}>Subject:</div>
                      <div style={{ fontSize: '16px', fontWeight: 600 }}>{preview.loading ? 'Loading preview...' : preview.subject}</div>
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                      {preview.error || (preview.loading ? '' : preview.body)}
                    </div>
                    {!preview.loading && !preview.error && preview.footer && (
                      <div
                        data-testid="email-preview-footer"
                        style={{
                          whiteSpace: 'pre-wrap', borderTop: '1px solid #e0e0e0', marginTop: '16px',
                          paddingTop: '10px', fontSize: '12px', color: 'var(--color-text-light)',
                        }}
                      >
                        {preview.footer}
                      </div>
                    )}
                  </div>
                  {preview.warning && (
                    <div className="alert alert-error" role="alert" data-testid="email-preview-warning">
                      {preview.warning.error}
                    </div>
                  )}
                </>
              )}

              {wantsText && split.text.length > 0 && (
                <>
                  {channel === 'both' && <h3 style={{ fontSize: '1rem', margin: '0 0 8px' }}>Text</h3>}
                  <div className="form-group">
                    <label className="form-label" htmlFor="smsPreviewAs">Preview text as</label>
                    <select
                      id="smsPreviewAs"
                      className="form-select"
                      value={smsPreviewRecipientId}
                      onChange={e => setSmsPreviewId(e.target.value)}
                    >
                      {split.text.map(r => (
                        <option key={r.id} value={String(r.id)}>
                          {`${r.first_name || ''} ${r.last_name || ''}`.trim() || `Customer ${r.id}`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div
                    data-testid="sms-preview"
                    style={{ background: '#e8f0fe', borderRadius: '16px', padding: '14px 16px', marginBottom: '6px', whiteSpace: 'pre-wrap', lineHeight: '1.5' }}
                  >
                    {smsPreview.error || (smsPreview.loading || !smsData ? 'Loading preview...' : (smsData.body || smsData.error))}
                  </div>
                  {smsData && smsData.body && <SmsSegmentCounter text={smsData.body} />}

                  {smsData && smsData.totals && (
                    <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '12px 16px', margin: '12px 0', fontSize: '14px' }}>
                      <div><strong>{smsData.totals.recipients}</strong> text{smsData.totals.recipients === 1 ? '' : 's'}, <strong>{smsData.totals.segments}</strong> segment{smsData.totals.segments === 1 ? '' : 's'} in all</div>
                      <div>Estimated cost: <strong>{dollars(smsData.totals.estimateCents)}</strong> <span style={{ color: 'var(--color-text-light)', fontSize: '12px' }}>(carrier fees vary; this is an estimate)</span></div>
                      {smsData.appended && <div style={{ color: 'var(--color-text-light)', fontSize: '13px' }}>&quot;Reply STOP to opt out.&quot; is added to the end of each text.</div>}
                      {Object.keys(smsData.totals.skipped || {}).length > 0 && (
                        <div style={{ color: 'var(--color-text-light)', fontSize: '13px' }}>Not texted: {reasonList(smsData.totals.skipped)}.</div>
                      )}
                      {smsData.dailyCap && smsData.dailyCap.applies && (
                        <div style={{ color: 'var(--color-text-light)', fontSize: '13px' }}>
                          Today: {smsData.dailyCap.usedToday} of {smsData.dailyCap.limit} segments used.
                        </div>
                      )}
                    </div>
                  )}

                  {smsData && smsData.quietHours?.quiet && (
                    <div className="alert alert-error" role="alert">
                      It is quiet hours. Bulk texts go out from 8:00 AM to 9:00 PM in your company's time zone; the next allowed time is {smsData.quietHours.nextAllowedText}.
                    </div>
                  )}
                  {smsData && smsData.sender && !smsData.sender.ok && (
                    <div className="alert alert-error" role="alert">{smsData.sender.error}</div>
                  )}
                  {smsData && smsData.dailyCap && !smsData.dailyCap.ok && (
                    <div className="alert alert-error" role="alert">
                      This send is {smsData.totals.segments} segments and only {smsData.dailyCap.remaining} of today&apos;s {smsData.dailyCap.limit} are left.
                    </div>
                  )}
                </>
              )}

              <div style={{
                background: '#fff3cd', padding: '12px 16px', borderRadius: '8px',
                border: '1px solid #ffc107', marginBottom: '16px',
              }}>
                <strong>
                  {channel === 'email' && `Ready to send to ${recipients.length} customers`}
                  {channel === 'text' && `Ready to text ${split.text.length} customers`}
                  {channel === 'both' && `Ready to send to ${recipients.length} customers: ${split.email.length} by email, ${split.text.length} by text`}
                </strong>
                <div style={{ fontSize: '13px', color: 'var(--color-text-light)', marginTop: '4px' }}>
                  This action cannot be undone.
                  {wantsEmail && ' Emails will be sent immediately.'}
                  {wantsText && ' Texts are queued and sent in the background over the next few minutes.'}
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Result */}
          {step === 4 && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              {sending && (
                <div>
                  <div className="spinner" style={{ margin: '0 auto 16px' }}></div>
                  <p>{wantsText ? 'Sending...' : 'Sending emails...'}</p>
                </div>
              )}
              {!sending && result && (
                <div>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>
                    {result.errors?.length > 0 ? '!' : String.fromCodePoint(0x2705)}
                  </div>
                  <h3>{result.message}</h3>
                  {result.errors?.length > 0 && (
                    <div style={{ marginTop: '12px', textAlign: 'left', background: '#fff3cd', padding: '12px', borderRadius: '8px' }}>
                      <strong>{result.errors.length} failed:</strong>
                      {result.errors.map((e, i) => (
                        <div key={i} style={{ fontSize: '13px', color: 'var(--color-text-light)' }}>{e.email}: {e.error}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {!sending && emailError && (
                <div className="alert alert-error" role="alert">The texts were queued, but the email part failed: {emailError}</div>
              )}
              {smsQueued && (
                <div style={{ marginTop: '16px', textAlign: 'left' }} data-testid="sms-batch-status">
                  <h3 style={{ fontSize: '1rem' }}>
                    Texts: {batch && batch.finished ? 'all handed to the carrier' : `${smsQueued.queued} queued, sending...`}
                  </h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', fontSize: '14px' }}>
                    <span>{batchCount('delivered')} delivered</span>
                    <span>{batch ? batchCount('pending', 'sending', 'queued', 'sent') : smsQueued.queued} queued</span>
                    <span>{batchCount('failed', 'undelivered')} failed</span>
                    <span>{batch ? batchCount('skipped') : Object.values(smsQueued.skipped || {}).reduce((a, b) => a + b, 0)} skipped</span>
                  </div>
                  {Object.keys((batch ? batch.skipped : smsQueued.skipped) || {}).length > 0 && (
                    <div style={{ fontSize: '13px', color: 'var(--color-text-light)', marginTop: '4px' }}>
                      Skipped: {reasonList(batch ? batch.skipped : smsQueued.skipped)}.
                    </div>
                  )}
                  <div style={{ fontSize: '12px', color: 'var(--color-text-light)', marginTop: '8px' }}>
                    You can close this window; texts keep sending. Delivery reports arrive over the next few minutes and are in Message History.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer with navigation buttons */}
        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between' }}>
          {step > 1 && step < 4 && (
            <button className="btn btn-outline" onClick={() => setStep(step - 1)}>Back</button>
          )}
          {step === 1 && <div />}

          <div style={{ display: 'flex', gap: '8px' }}>
            {step < 4 && (
              <button className="btn btn-outline" onClick={onClose}>Cancel</button>
            )}
            {step === 1 && (
              <button
                className="btn btn-primary"
                disabled={recipients.length === 0}
                onClick={() => { setError(''); setStep(2); }}
              >
                Next: Compose
              </button>
            )}
            {step === 2 && (
              <button
                className="btn btn-primary"
                disabled={!composeReady}
                onClick={() => { setError(''); setStep(3); }}
              >
                Next: Preview
              </button>
            )}
            {step === 3 && (
              <button className="btn btn-primary" onClick={handleSend} disabled={sending || smsBlocked}>
                {sendLabel}
              </button>
            )}
            {step === 4 && !sending && (
              <button className="btn btn-primary" onClick={onClose}>Done</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmailCustomersModal;
